import React from "react";
import LocationPicker from "./LocationPicker.js";

/**
 * Pick a user's default location (users.default_location_id) — the location
 * every page's location selector starts on. Options are the user's assigned
 * locations; with none assigned (unscoped user) any location can be chosen.
 *
 * Props:
 *   allLocations – [{ location_id, location_name, location_code }]
 *   assigned     – number[]  assigned location ids
 *   value        – number | null
 *   onChange     – (id: number | null) => void
 */
function DefaultLocationSelect({ allLocations = [], assigned = [], value, onChange, disabled }) {
    // Inactive / TRANSIT locations are never offered by pages, so they can't be a default.
    const eligible = allLocations.filter(isEligible);
    const options = assigned.length
        ? eligible.filter((l) => assigned.includes(l.location_id))
        : eligible;

    return (
        <LocationPicker
            locations={options}
            value={value ?? ""}
            onChange={(v) => onChange(v ? Number(v) : null)}
            emptyLabel="No default"
            ariaLabel="Default location"
            fullWidth
            disabled={disabled}
        />
    );
}

const isEligible = (l) => l && l.is_active !== false && l.location_type !== "TRANSIT";

/**
 * Keep a default consistent with the assigned set: drop it when it's no longer
 * assigned (or not eligible), and default to the only eligible assigned location.
 * allLocations (optional) supplies location_type / is_active for eligibility.
 */
export const reconcileDefaultLocation = (current, assigned = [], allLocations = null) => {
    const byId = allLocations ? new Map(allLocations.map((l) => [l.location_id, l])) : null;
    const ok = (id) => !byId || !byId.has(id) || isEligible(byId.get(id));
    if (!assigned.length) return current != null && !ok(current) ? null : current;
    const eligibleAssigned = assigned.filter(ok);
    if (current != null && eligibleAssigned.includes(current)) return current;
    return eligibleAssigned.length === 1 ? eligibleAssigned[0] : null;
};

export default DefaultLocationSelect;
